import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MyApiClient, { impostaProfiloDiModifica } from '../../../../api-client/MyApiClient';
import { SUPERADMIN_PUO_MODIFICARE } from '../../Util/modificaSuperAdmin';
import { segnalaMancanti } from '../../Util/campiMancanti';
import { getErrorLabel } from '../../Util/errorMessage';
import { bloodTypes, foodAllergies, foodIntollerances, traumas, medicalOperations } from '../../Util/enum';

/*
 * Il super admin modifica l'anagrafica di un player, di un maestro o di un junior (schede in superadmin/anagrafiche).
 *
 * - Acceso solo con SUPERADMIN_PUO_MODIFICARE (REACT_APP_SUPERADMIN_MODIFICA=S): spento, le schede restano in sola lettura.
 * - Con la scheda aperta `impostaProfiloDiModifica(id)` manda il salvataggio a PATCH /administration/profiles/{id}.
 * - Il profilo completo si legge da GET /profile?userId= (PersonalProfile: idsPlay, idsGuru o junior).
 * - Il backend aggiorna solo i campi ricevuti: si mandano SOLO quelli cambiati, da tutti i tab insieme.
 *   Mai password, email di accesso o username (sono le credenziali: restano fuori per scelta).
 *
 * Corpo: player { idsPlay: {...} }, maestro { idsGuru: {...} }, junior { junior: {...} } (senza userId: vale quello del percorso).
 */

const RAMO_CORPO = { player: 'idsPlay', maestro: 'idsGuru', junior: 'junior' };

// Gruppi di campi = tab della scheda (la pagina dice quale tab apre ciascun gruppo)
export const GRUPPI = ['anagrafica', 'sport', 'salute', 'forma'];

export const lingue = [
    { value: 'ar', label: 'Arabo' },
    { value: 'de', label: 'Tedesco' },
    { value: 'en', label: 'Inglese' },
    { value: 'it', label: 'Italiano' },
    { value: 'es', label: 'Spagnolo' },
    { value: 'fr', label: 'Francese' },
    { value: 'ru', label: 'Russo' },
];

export const sessi = [
    { value: 'MALE', label: 'Maschio' },
    { value: 'FEMALE', label: 'Femmina' },
];

export const mani = [
    { value: 'RIGHT', label: 'Destra' },
    { value: 'LEFT', label: 'Sinistra' },
    { value: 'AMBIDEXTROUS', label: 'Ambidestro' },
];

export const piedi = [
    { value: 'RIGHT', label: 'Destro' },
    { value: 'LEFT', label: 'Sinistro' },
    { value: 'AMBIDEXTROUS', label: 'Ambidestro' },
];

// Valori ammessi dal backend (openapi: Trauma, MedicalOperation, FoodAllergy, FoodIntollerance): l'elenco di enum.js
// ha qualche voce in piu' che il server rifiuterebbe.
const TRAUMI_AMMESSI = ['HEAD', 'NECK', 'CHEST', 'ABDOMEN', 'PELVIS', 'UPPER_BACK', 'LOWER_BACK', 'LEFT_LEG', 'RIGHT_LEG', 'LEFT_FOOT', 'RIGHT_FOOT',
    'LEFT_ANKLE', 'RIGHT_ANKLE', 'LEFT_WRIST', 'RIGHT_WRIST', 'LEFT_HAND', 'RIGHT_HAND', 'LEFT_ELBOW', 'RIGHT_ELBOW', 'LEFT_KNEE', 'RIGHT_KNEE',
    'LEFT_ARM', 'RIGHT_ARM', 'LEFT_GROIN', 'RIGHT_GROIN', 'OTHER'];
const INTERVENTI_AMMESSI = ['HEAD', 'NECK', 'CHEST', 'ABDOMEN', 'PELVIS', 'UPPER_BACK', 'LOWER_BACK', 'SKIN', 'LEFT_LEG', 'RIGHT_LEG', 'LEFT_FOOT',
    'RIGHT_FOOT', 'LEFT_ANKLE', 'RIGHT_ANKLE', 'LEFT_WRIST', 'RIGHT_WRIST', 'LEFT_HAND', 'RIGHT_HAND', 'OTHER'];
// In enum.js SHELLFISH e MOLLUSCS hanno la stessa etichetta: qui si distinguono
const alimenti = (elenco) => elenco.map((o) => (o.value === 'SHELLFISH' ? { ...o, label: 'Crostacei' } : o));

export const opzioniElenchi = {
    spokenLocales: lingue,
    foodAllergies: alimenti(foodAllergies),
    foodIntollerances: alimenti(foodIntollerances),
    traumas: traumas.filter((o) => TRAUMI_AMMESSI.includes(o.value)),
    medicalOperations: medicalOperations.filter((o) => INTERVENTI_AMMESSI.includes(o.value)),
};
export const gruppiSanguigni = bloodTypes;

// Campi per tipo di scheda (JuniorUpdate non ha telefono, professione, interventi e malattie ereditarie)
const TESTI = ['givenName', 'familyName', 'birthdate', 'nationality', 'nation', 'taxCode', 'description'];
const TESTI_SENIOR = ['phoneNumber', 'profession'];
const TESTI_MAESTRO = ['vatNumber', 'sdi'];
const SCELTE = ['gender', 'primaryHand', 'primaryFoot', 'bloodType'];
const ELENCHI = ['spokenLocales', 'foodAllergies', 'foodIntollerances', 'traumas', 'medicines'];
const ELENCHI_SENIOR = ['medicalOperations', 'hereditaryDiseases'];
const ELENCHI_CON_VALORI_FISSI = ['foodAllergies', 'foodIntollerances', 'traumas', 'medicalOperations'];
const NUMERI = ['averageHeartRate', 'averageLowBloodPressure', 'averageHighBloodPressure'];
const INDIRIZZO = ['streetAddress', 'locality', 'province', 'postalCode', 'country'];
const FORMA = ['weight', 'height', 'footNumber'];

const testiDi = (tipo) => [...TESTI, ...(tipo === 'junior' ? [] : TESTI_SENIOR), ...(tipo === 'maestro' ? TESTI_MAESTRO : [])];
const elenchiDi = (tipo) => [...ELENCHI, ...(tipo === 'junior' ? [] : ELENCHI_SENIOR)];

const testo = (v) => (v === undefined || v === null ? '' : String(v));
const elenco = (v) => (Array.isArray(v) ? v.filter((x) => x !== null && x !== undefined && x !== '').map(String) : []);
const numero = (v) => Number(String(v).trim().replace(',', '.'));

/** Lo stato fisico attuale: il piu' recente (come la vista «Forma fisica»). */
export const statoAttuale = (states) => (Array.isArray(states) && states.length > 0
    ? states.reduce((a, b) => ((a.updatedAt || '') >= (b.updatedAt || '') ? a : b))
    : null);

/** Il ramo del profilo personale da modificare. */
const ramoDelProfilo = (tipo, r) => {
    if (!r) return null;
    if (tipo === 'maestro') return r.idsGuru || null;
    if (tipo === 'junior') return r.junior || null;
    return r.idsPlay || r.idsGuru || null;
};

/** I valori dei campi della scheda, letti dal profilo. */
export const daProfilo = (tipo, p) => {
    const indirizzo = p.address || {};
    const stato = statoAttuale(p.states) || {};
    const nascita = testo(p.birthdate);
    const dati = {
        givenName: testo(p.givenName),
        familyName: testo(p.familyName),
        birthdate: /^\d{4}-\d{2}-\d{2}/.test(nascita) ? nascita.slice(0, 10) : nascita,
        nationality: testo(p.nationality),
        nation: testo(p.nation),
        taxCode: testo(p.taxCode),
        description: testo(tipo === 'maestro' ? (p.description || p.guruDescriprion) : p.description),
        gender: testo(p.gender),
        primaryHand: testo(p.primaryHand),
        primaryFoot: testo(p.primaryFoot),
        bloodType: testo(p.bloodType),
        insured: p.insured === true,
        streetAddress: testo(indirizzo.streetAddress),
        locality: testo(indirizzo.locality),
        province: testo(indirizzo.province),
        postalCode: testo(indirizzo.postalCode),
        country: testo(indirizzo.country),
        weight: testo(stato.weight),
        height: testo(stato.height),
        footNumber: testo(stato.footNumber),
        sports: (p.sports || []).filter((s) => s && s.sportType).map((s) => ({ sportType: s.sportType, sportRole: testo(s.sportRole) })),
    };
    testiDi(tipo).forEach((k) => { if (!(k in dati)) dati[k] = testo(p[k]); });
    elenchiDi(tipo).forEach((k) => { dati[k] = elenco(p[k]); });
    NUMERI.forEach((k) => { dati[k] = testo(p[k]); });
    if (tipo === 'maestro') {
        dati.professionalPhone = testo(p.professionalContacts?.phoneNumber);
        dati.professionalEmail = testo(p.professionalContacts?.email);
    }
    return dati;
};

/** Per ogni sport del profilo: se e' insegnato (ha un tesserino) e la percentuale dei livelli. */
const infoSportDi = (p) => Object.fromEntries((p.sports || []).filter((s) => s && s.sportType).map((s) => [s.sportType, {
    insegnato: !!s.license,
    livello: typeof s.percentageOfLevelsCompletion === 'number' ? s.percentageOfLevelsCompletion : null,
}]));

/** Solo i campi cambiati, nella forma di IDSPlayUpdate / IDSGuruUpdate / JuniorUpdate. */
export const corpoModifiche = (tipo, prima, dopo) => {
    const cambiato = (k) => JSON.stringify(prima[k]) !== JSON.stringify(dopo[k]);
    const c = {};
    testiDi(tipo).forEach((k) => {
        if (!cambiato(k)) return;
        const v = k === 'description' ? dopo[k] : dopo[k].trim();
        c[k] = k === 'taxCode' ? v.toUpperCase() : v;
    });
    // Le tendine non si svuotano: un valore vuoto non parte (lo schema accetta solo i valori dell'elenco)
    SCELTE.forEach((k) => { if (cambiato(k) && dopo[k]) c[k] = dopo[k]; });
    elenchiDi(tipo).forEach((k) => { if (cambiato(k)) c[k] = dopo[k].map((v) => String(v).trim()).filter(Boolean); });
    NUMERI.forEach((k) => { if (cambiato(k) && String(dopo[k]).trim() !== '') c[k] = parseInt(dopo[k], 10); });
    if (cambiato('insured')) c.insured = !!dopo.insured;
    // L'indirizzo e' un oggetto unico (streetAddress, locality e country obbligatori): si manda intero
    if (INDIRIZZO.some(cambiato)) c.address = Object.fromEntries(INDIRIZZO.map((k) => [k, dopo[k].trim()]));
    // Stato fisico: un solo stato, quello attuale, come fanno le app (il server ricalcola BMI e classe)
    if (FORMA.some(cambiato)) c.states = [{ weight: numero(dopo.weight), height: numero(dopo.height), footNumber: numero(dopo.footNumber) }];
    // Sport praticati: l'elenco si manda intero (sostituisce quello di prima); il ruolo solo se c'e'
    if (cambiato('sports')) c.sports = dopo.sports.map((s) => (s.sportRole ? { sportType: s.sportType, sportRole: s.sportRole } : { sportType: s.sportType }));
    // Contatti professionali del maestro: oggetto con email e telefono, entrambi obbligatori nello schema
    if (tipo === 'maestro' && (cambiato('professionalEmail') || cambiato('professionalPhone'))) {
        c.professionalContacts = { email: dopo.professionalEmail.trim(), phoneNumber: dopo.professionalPhone.trim() };
    }
    return c;
};

const oggiIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const etaInAnni = (iso) => {
    const [a, m, g] = iso.split('-').map(Number);
    const oggi = new Date();
    let eta = oggi.getFullYear() - a;
    if (oggi.getMonth() + 1 < m || (oggi.getMonth() + 1 === m && oggi.getDate() < g)) eta -= 1;
    return eta;
};

/** Limiti della data di nascita per il campo (junior: da 0 a 17 anni). */
export const limitiNascita = (tipo) => {
    const oggi = oggiIso();
    if (tipo !== 'junior') return { min: '1900-01-01', max: oggi };
    const [a, m, g] = oggi.split('-').map(Number);
    const primo = new Date(a - 18, m - 1, g + 1);
    return { min: `${primo.getFullYear()}-${String(primo.getMonth() + 1).padStart(2, '0')}-${String(primo.getDate()).padStart(2, '0')}`, max: oggi };
};

const erroreNascita = (tipo, v) => {
    if (!v) return 'Manca la data di nascita';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(new Date(v).getTime())) return 'La data di nascita non è valida';
    if (v > oggiIso()) return 'La data di nascita non può essere nel futuro';
    if (v < '1900-01-01') return 'La data di nascita non è valida';
    if (tipo === 'junior' && etaInAnni(v) > 17) return 'Il junior deve avere meno di 18 anni';
    return null;
};

const NOMI_FORMA = { weight: 'il peso', height: "l'altezza", footNumber: 'il numero di piede' };
const FORMA_NON_VALIDA = { weight: 'Il peso non è valido', height: "L'altezza non è valida", footNumber: 'Il numero di piede non è valido' };

/** Dati mancanti o sbagliati: [{ gruppo, campo, messaggio }], nell'ordine dei tab. */
export const controlli = (tipo, prima, f) => {
    const errori = [];
    const segna = (gruppo, campo, messaggio) => errori.push({ gruppo, campo: `sa-${campo}`, messaggio });
    const cambiato = (k) => JSON.stringify(prima[k]) !== JSON.stringify(f[k]);

    if (!f.givenName.trim()) segna('anagrafica', 'givenName', 'Manca il nome');
    if (!f.familyName.trim()) segna('anagrafica', 'familyName', 'Manca il cognome');
    // La data si controlla quando cambia (il junior la deve avere sempre)
    if (cambiato('birthdate') || (tipo === 'junior' && !f.birthdate)) {
        const msg = erroreNascita(tipo, f.birthdate);
        if (msg) segna('anagrafica', 'birthdate', msg);
    }
    if (cambiato('taxCode') && f.taxCode.trim() && !/^[A-Za-z0-9]{16}$/.test(f.taxCode.trim())) {
        segna('anagrafica', 'taxCode', 'Il codice fiscale deve avere 16 caratteri, lettere e cifre');
    }
    if (tipo === 'maestro' && (cambiato('professionalEmail') || cambiato('professionalPhone'))) {
        const email = f.professionalEmail.trim();
        if (!email) segna('anagrafica', 'professionalEmail', "Manca l'email professionale");
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) segna('anagrafica', 'professionalEmail', "L'email professionale non è valida");
    }

    NUMERI.forEach((k) => {
        if (!cambiato(k)) return;
        const v = String(f[k]).trim();
        if (v === '') segna('salute', k, 'Il valore non si può togliere: scrivi un numero');
        else if (!/^\d+$/.test(v)) segna('salute', k, 'Scrivi un numero intero');
    });
    ELENCHI_CON_VALORI_FISSI.forEach((k) => {
        if (!(k in f) || !cambiato(k)) return;
        const ammessi = opzioniElenchi[k].map((o) => o.value);
        const fuori = f[k].filter((v) => !ammessi.includes(v));
        if (fuori.length > 0) segna('salute', k, `${fuori.map((v) => `«${v}»`).join(', ')} non è più previsto: toglilo dall'elenco`);
    });

    if (FORMA.some(cambiato)) {
        FORMA.forEach((k) => {
            const v = String(f[k]).trim();
            const n = numero(v);
            if (v === '') segna('forma', k, `Manca ${NOMI_FORMA[k]}`);
            else if (Number.isNaN(n) || n < 0 || (k !== 'footNumber' && n === 0)) segna('forma', k, FORMA_NON_VALIDA[k]);
        });
        // Senza il sesso il server non calcola il BMI e scarta il nuovo stato fisico
        if (!f.gender) segna('anagrafica', 'gender', 'Manca il sesso: serve per calcolare il BMI');
    }

    return errori.sort((a, b) => GRUPPI.indexOf(a.gruppo) - GRUPPI.indexOf(b.gruppo));
};

const leggiToken = () => {
    try {
        return JSON.parse(sessionStorage.getItem('userDetails')).idToken;
    } catch (e) {
        return '';
    }
};

/**
 * Stato della modifica di una scheda.
 *   const m = useModificaAnagrafica(id, 'player', '/superadmin/anagrafiche/player');
 *   m.pronto: i campi si possono modificare (interruttore acceso e profilo letto)
 *   m.f, m.imposta(chiave, valore), m.modificato, m.salva(vaiAlGruppo), m.annulla(), m.errore, m.chiudiErrore()
 *   m.profiloPrivato: una copia del profilo personale, per i riquadri in sola lettura (certificati)
 */
export const useModificaAnagrafica = (id, tipo, lista) => {
    const attivo = SUPERADMIN_PUO_MODIFICARE;
    const navigate = useNavigate();
    const [iniziale, setIniziale] = useState(null);
    const [f, setF] = useState(null);
    const [infoSport, setInfoSport] = useState({});
    const [profiloPrivato, setProfiloPrivato] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState('');

    // Con la scheda aperta il salvataggio del profilo va alla rotta di amministrazione di questo utente
    useEffect(() => {
        if (!attivo) return undefined;
        impostaProfiloDiModifica(id);
        return () => impostaProfiloDiModifica(null);
    }, [attivo, id]);

    useEffect(() => {
        if (!attivo) return undefined;
        let vivo = true;
        setIniziale(null);
        setF(null);
        (async () => {
            try {
                const r = await new MyApiClient().getPrivateProfile(leggiToken(), id);
                if (!vivo) return;
                const p = ramoDelProfilo(tipo, r);
                if (!p) return;
                // Copie separate: i riquadri in sola lettura cambiano l'oggetto che ricevono
                const copia = JSON.parse(JSON.stringify(p));
                const dati = daProfilo(tipo, copia);
                setIniziale(dati);
                setF(dati);
                setInfoSport(infoSportDi(copia));
                setProfiloPrivato(JSON.parse(JSON.stringify(r)));
            } catch (e) {
                // Senza il profilo personale la scheda resta in sola lettura
                console.error('Profilo personale non disponibile:', e);
            }
        })();
        return () => { vivo = false; };
    }, [attivo, id, tipo]);

    const imposta = useCallback((chiave, valore) => setF((prima) => ({ ...prima, [chiave]: valore })), []);

    const pronto = attivo && !!f && !!iniziale;
    const modificato = pronto && JSON.stringify(f) !== JSON.stringify(iniziale);

    const annulla = () => navigate(lista);

    // Un solo Salva per tutta la scheda: manda insieme i campi cambiati in qualsiasi tab, poi torna all'elenco
    const salva = async (vaiAlGruppo) => {
        if (!pronto || inCorso) return;
        const errori = controlli(tipo, iniziale, f);
        if (errori.length > 0) {
            // Si apre il tab del primo campo da sistemare, poi si segnalano i campi (il tab deve essere visibile)
            if (vaiAlGruppo) vaiAlGruppo(errori[0].gruppo);
            setTimeout(() => segnalaMancanti(errori), 80);
            return;
        }
        const modifiche = corpoModifiche(tipo, iniziale, f);
        if (Object.keys(modifiche).length === 0) {
            navigate(lista);
            return;
        }
        setInCorso(true);
        try {
            const r = await new MyApiClient().updateProfile(leggiToken(), { [RAMO_CORPO[tipo]]: modifiche });
            if (!r || r.error) {
                const motivo = r && r.error ? getErrorLabel(r.error) : '';
                setErrore(motivo || 'Il server non ha risposto: riprova.');
                return;
            }
            navigate(lista);
        } finally {
            setInCorso(false);
        }
    };

    return {
        attivo, pronto, f, imposta, infoSport, profiloPrivato, modificato, inCorso, salva, annulla,
        errore, chiudiErrore: () => setErrore(''),
    };
};

/**
 * Annulla e Salva, a destra in fondo alla scheda (Elimina resta a sinistra). Salva e' sempre premibile (docs/sportid-ui.md):
 * senza modifiche torna all'elenco senza chiamare il server.
 */
export const TastiModifica = ({ modifica, vaiAlGruppo }) => {
    if (!modifica.pronto) return null;
    return (
        <div className="d-flex gap-2 ms-auto">
            <Button className="btn btn-light btn-add" onClick={modifica.annulla} disabled={modifica.inCorso}>Annulla</Button>
            <Button className="btn btn-primary btn-add" onClick={() => modifica.salva(vaiAlGruppo)} disabled={modifica.inCorso}>Salva</Button>
        </div>
    );
};

/** Il server ha rifiutato il salvataggio: si resta nella scheda e si legge il motivo. */
export const FinestraModificaNonSalvata = ({ modifica }) => (
    <Modal className="fade" show={!!modifica.errore} onHide={modifica.chiudiErrore} centered>
        <Modal.Header closeButton>
            <Modal.Title>Modifica non salvata</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modifica.errore}</Modal.Body>
        <Modal.Footer className="justify-content-center">
            <Button className="btn btn-primary btn-add" onClick={modifica.chiudiErrore}>Chiudi</Button>
        </Modal.Footer>
    </Modal>
);
